import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Loader2, Sparkles } from "lucide-react";

import { flattenAgenda } from "@/pages/dashboard/dashboardAlertsModel";
import {
  getFiscalAgenda,
  getRecentAutomations,
  type AutomationEvent,
} from "@/services/fiscalService";

interface DashboardPreparedByFiscaloProps {
  recurringDraftsReadyCount: number;
  showPaidFiscalDashboard: boolean;
  hasIvaEstimate: boolean;
}

interface AutomationItem {
  key: string;
  text: string;
  tone: "success" | "danger" | "info";
}

export function DashboardPreparedByFiscalo({
  recurringDraftsReadyCount,
  showPaidFiscalDashboard,
  hasIvaEstimate,
}: DashboardPreparedByFiscaloProps): JSX.Element | null {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const { data: agendaGrouped, isSuccess: agendaLoaded } = useQuery({
    queryKey: ["fiscal-agenda", year + "-01", year + "-12"],
    queryFn: () => getFiscalAgenda(year + "-01", year + "-12"),
    enabled: showPaidFiscalDashboard,
  });

  // Recent automated actions for the "Fiscalo trabajó por ti" feed. Cheap query, only enabled
  // for paid plans. 14-day window keeps the list fresh without dropping post-cron events.
  const { data: recentAutomations } = useQuery({
    queryKey: ["recent-automations", 14],
    queryFn: () => getRecentAutomations(14),
    enabled: showPaidFiscalDashboard,
    staleTime: 60_000,
  });

  const hasUpcomingAgenda = useMemo(() => {
    if (!agendaGrouped) return false;
    return (
      flattenAgenda(agendaGrouped).filter((o) => o.status !== "completed")
        .length > 0
    );
  }, [agendaGrouped]);

  const items: AutomationItem[] = [];

  // Recent boleto/BPS automation events come first — they're concrete actions Fiscalo took.
  for (const ev of recentAutomations ?? []) {
    items.push(automationEventToItem(ev, t));
  }

  if (hasIvaEstimate) {
    items.push({
      key: "iva",
      text: t("dashboard.preparedByFiscalo.iva"),
      tone: "success",
    });
  }
  if (showPaidFiscalDashboard && agendaLoaded && hasUpcomingAgenda) {
    items.push({
      key: "agenda",
      text: t("dashboard.preparedByFiscalo.agenda"),
      tone: "success",
    });
  }
  if (recurringDraftsReadyCount > 0) {
    items.push({
      key: "recurring",
      text: t("dashboard.preparedByFiscalo.recurring", {
        count: recurringDraftsReadyCount,
      }),
      tone: "success",
    });
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="fx-panel">
      <div className="fx-panel-head">
        <div className="title">
          <span className="ico-tile ink">
            <Sparkles className="h-4 w-4" />
          </span>
          <span>{t("dashboard.preparedByFiscalo.title")}</span>
        </div>
        <span className="right-meta">
          {t("dashboard.preparedByFiscalo.auto", "Automático")}
        </span>
      </div>
      <ul className="fx-issues">
        {items.map((row) => (
          <li key={row.key} className="fx-issue">
            <span className={`ico ${row.tone === "danger" ? "danger" : row.tone === "info" ? "info" : "success"}`}>
              {row.tone === "danger" ? (
                <AlertCircle className="h-3.5 w-3.5" />
              ) : row.tone === "info" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
            </span>
            <span className="txt">{row.text}</span>
            <span />
          </li>
        ))}
      </ul>
    </div>
  );
}

function automationEventToItem(
  ev: AutomationEvent,
  t: (key: string, opts?: Record<string, unknown>) => string,
): AutomationItem {
  const period = ev.period ?? "";
  const taxLabel = ev.taxLabel ?? "IVA";
  const key = `${ev.kind}-${ev.obligationId ?? ev.occurredAt}`;
  switch (ev.kind) {
    case "boleto-auto-generated":
      return {
        key,
        tone: "success",
        text: t("dashboard.preparedByFiscalo.boletoAutoGenerated", {
          taxLabel,
          period,
          defaultValue: "Generamos tu boleto de pago de {{taxLabel}} ({{period}}) y te lo enviamos por email.",
        }),
      };
    case "boleto-auto-failed":
      return {
        key,
        tone: "danger",
        text: t("dashboard.preparedByFiscalo.boletoAutoFailed", {
          taxLabel,
          period,
          defaultValue: "No pudimos auto-generar tu boleto de {{taxLabel}} ({{period}}) — te toca generarlo manualmente.",
        }),
      };
    case "boleto-auto-in-progress":
      return {
        key,
        tone: "info",
        text: t("dashboard.preparedByFiscalo.boletoAutoInProgress", {
          taxLabel,
          period,
          defaultValue: "Generando automáticamente tu boleto de {{taxLabel}} ({{period}})…",
        }),
      };
    case "bps-doc-auto-requested":
      return {
        key,
        tone: "success",
        text: t("dashboard.preparedByFiscalo.bpsDocAutoRequested", {
          period,
          defaultValue: "Le pedimos a tu contador los documentos de BPS del {{period}}.",
        }),
      };
  }
}
